import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import Autocomplete from '../ui/autocomplete';
import { 
  Brain, 
  Target, 
  PlayCircle,
  Award,
  BarChart3,
  Link,
  MessageCircle,
  Zap,
  Heart,
  FileText,
  Users,
  Search
} from 'lucide-react';

// Importar componentes modulares
import DiagnosticoIA from './DiagnosticoIA';
import BibliotecaRecursos from './BibliotecaRecursosNova';
import GamificacaoPDI from './GamificacaoPDI';
import SistemaMentoria from './SistemaMentoria';
import TimelineAcompanhamento from './TimelineAcompanhamentoIA';
import KanbanPDI from './KanbanPDIIA';
import AvaliacaoResultados from './AvaliacaoResultados';
import DashboardAnalyticsPDI from './DashboardAnalyticsPDI';
import InterfaceRelatorios from './InterfaceRelatorios';
import ReflexoesPessoais from './ReflexoesPessoais';
import NotificacoesPDI from './NotificacoesPDI';

const API = process.env.REACT_APP_BACKEND_URL;

const PDICompletoMain = () => {
  const [currentTab, setCurrentTab] = useState('diagnostico');
  const [pdiAtual, setPdiAtual] = useState(null);
  const [colaboradorSelecionado, setColaboradorSelecionado] = useState(null);

  const modulosCompletos = [
    {
      id: 'diagnostico',
      nome: 'Diagnóstico IA',
      descricao: 'Análise inteligente de necessidades de desenvolvimento',
      icon: Brain,
      color: 'text-purple-600',
      component: DiagnosticoIA,
      modulo: 1
    },
    {
      id: 'planejamento',
      nome: 'Biblioteca de Recursos',
      descricao: 'Catálogo de ações de desenvolvimento',
      icon: Target,
      color: 'text-blue-600',
      component: BibliotecaRecursos,
      modulo: 2
    },
    {
      id: 'notificacoes',
      nome: 'Notificações',
      descricao: 'Sistema de notificações push e preferências',
      icon: PlayCircle,
      color: 'text-orange-600',
      component: NotificacoesPDI,
      modulo: 3
    },
    {
      id: 'execucao',
      nome: 'Timeline',
      descricao: 'Timeline de execução e acompanhamento',
      icon: PlayCircle,
      color: 'text-green-600',
      component: TimelineAcompanhamento,
      modulo: 4
    },
    {
      id: 'kanban',
      nome: 'Kanban PDI',
      descricao: 'Gestão visual de ações',
      icon: PlayCircle,
      color: 'text-cyan-600',
      component: KanbanPDI,
      modulo: 5
    },
    {
      id: 'avaliacao',
      nome: 'Avaliação de Resultados',
      descricao: 'Mensuração de evolução e impacto',
      icon: Award,
      color: 'text-yellow-600',
      component: AvaliacaoResultados,
      modulo: 6
    },
    {
      id: 'analytics',
      nome: 'Dashboard Analytics',
      descricao: 'Visão executiva e insights estratégicos',
      icon: BarChart3,
      color: 'text-indigo-600',
      component: DashboardAnalyticsPDI,
      modulo: 7
    },
    {
      id: 'mentoria',
      nome: 'Sistema de Mentoria',
      descricao: 'Matching e gestão de mentorias',
      icon: MessageCircle,
      color: 'text-pink-600',
      component: SistemaMentoria,
      modulo: 8
    },
    {
      id: 'reflexoes',
      nome: 'Reflexões Pessoais',
      descricao: 'Diário de desenvolvimento e aprendizados',
      icon: Heart,
      color: 'text-rose-600',
      component: ReflexoesPessoais,
      modulo: 9
    },
    {
      id: 'gamificacao',
      nome: 'Gamificação',
      descricao: 'Engajamento e reconhecimento',
      icon: Zap,
      color: 'text-orange-600',
      component: GamificacaoPDI,
      modulo: 10
    },
    {
      id: 'relatorios',
      nome: 'Relatórios',
      descricao: 'PDFs, Excel e auditoria',
      icon: FileText,
      color: 'text-red-600',
      component: InterfaceRelatorios,
      modulo: 11
    }
  ];

  const handleDiagnosticoConcluido = (competenciasSelecionadas) => {
    console.log('Diagnóstico concluído com competências:', competenciasSelecionadas);
    toast.success(`${competenciasSelecionadas.length} competências adicionadas ao PDI!`);
    // Aqui você pode salvar no backend se necessário
  };

  const renderModulo = () => {
    const moduloAtivo = modulosCompletos.find(m => m.id === currentTab);
    if (!moduloAtivo) return null;

    const ComponenteModulo = moduloAtivo.component;
    return (
      <ComponenteModulo 
        pdiId={pdiAtual?.id} 
        colaboradorId={colaboradorSelecionado?.id}
        onDiagnosticoConcluido={handleDiagnosticoConcluido}
      />
    );
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Tela de Seleção de Colaborador */}
      {!colaboradorSelecionado ? (
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="bg-gradient-to-r from-purple-500 to-blue-500 text-white">
            <CardTitle className="flex items-center space-x-3">
              <Users className="h-8 w-8" />
              <div>
                <h2 className="text-2xl font-bold">Selecione um Colaborador</h2>
                <p className="text-sm text-white/90 font-normal">
                  Escolha o colaborador para criar ou visualizar o PDI
                </p>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Colaborador *
              </label>
              
              
              <Autocomplete
                value={colaboradorSelecionado?.id || ''}
                onValueChange={(value, item) => {
                  if (item) setColaboradorSelecionado(item);
                }}
                endpoint={`${API}/api/employees-paged`}
                searchParam="search"
                getOptionLabel={(item) => `${item.nome}${item.cargo ? ` · ${item.cargo}` : ''}${item.departamento ? ` · ${item.departamento}` : ''}`}
                getOptionValue={(item) => item.id}
                placeholder="Digite o nome, CPF ou cargo..."
                extraParams={{ active_only: true }}
                emptyMessage="Nenhum colaborador encontrado"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Search className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">💡 Dica</p>
                  <p>
                    Após selecionar o colaborador, você terá acesso aos 11 módulos do PDI completo, 
                    incluindo diagnóstico com IA, biblioteca de recursos, kanban de ações e muito mais.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Header com colaborador selecionado */}
          <Card className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 border-2 border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                    PDI Completo - 11 Módulos
                  </h1>
                  <p className="text-gray-600 flex items-center space-x-2">
                    <Users className="h-4 w-4" />
                    <span>
                      <strong>{colaboradorSelecionado.nome}</strong> • {colaboradorSelecionado.cargo || 'Sem cargo'}
                    </span>
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Sistema PDI</div>
                    <div className="text-lg font-bold text-purple-600">100% Completo</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full">
                    <Brain className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setColaboradorSelecionado(null)}
                className="mt-4"
              >
                Trocar Colaborador
              </Button>
            </CardContent>
          </Card>
          
          {/* Grid de Módulos */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {modulosCompletos.map(modulo => {
          const IconComponent = modulo.icon;
          return (
            <Card 
              key={modulo.id}
              className={`cursor-pointer hover:shadow-xl transition-all border-2 ${
                currentTab === modulo.id 
                  ? 'border-purple-500 bg-purple-50' 
                  : 'border-gray-200 hover:border-purple-300'
              }`}
              onClick={() => setCurrentTab(modulo.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start space-x-3">
                  <div className={`p-2 bg-gray-100 rounded-lg ${modulo.color}`}>
                    <IconComponent className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-sm">{modulo.nome}</h3>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        M{modulo.modulo}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{modulo.descricao}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Conteúdo do Módulo Selecionado */}
      <Card>
        <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-blue-50">
          <CardTitle className="flex items-center space-x-2">
            {(() => {
              const modulo = modulosCompletos.find(m => m.id === currentTab);
              if (!modulo) return null;
              const IconComponent = modulo.icon;
              return (
                <>
                  <IconComponent className={`h-6 w-6 ${modulo.color}`} />
                  <span>{modulo.nome}</span>
                  <span className="text-sm font-normal text-gray-600">
                    - {modulo.descricao}
                  </span>
                </>
              );
            })()}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {renderModulo()}
        </CardContent>
      </Card>

      {/* Footer com Status */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-green-700">✅ Sistema PDI 100% Completo</p>
              <p className="text-xs text-gray-600">
                Todos os 11 módulos implementados e integrados com IA
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                Produção
              </span>
              <span className="px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full">
                IA Ativa
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
};

export default PDICompletoMain;
